import React, { useState } from 'react';
import Modal from '../../../BaseComponents/Modal';
import { Formik } from 'formik';
import TextArea from '../../../BaseComponents/TextArea';
import './TagModal.scss';
import Button from '../../../BaseComponents/Button';
import TextInput from '../../../BaseComponents/TextInput';
import { toast } from 'react-toastify';
import { createTag, updateTag } from '../../../../api/tags';
import { Tag } from '../../../../types/apiGeneratedTypes';

type TagsModalProps = {
  isOpen: boolean;
  tagToEdit: Tag | null;
  onClose: () => void;
  onSaved: () => void;
};

interface TagFormValues {
  name: string;
  description: string;
  color: string;
}

const TagsModal: React.FC<TagsModalProps> = ({
  isOpen,
  tagToEdit,
  onClose,
  onSaved,
}) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<any>(null);

  const getDefaultValues = (): TagFormValues => {
    if (tagToEdit) {
      return {
        name: tagToEdit.name ?? '',
        description: tagToEdit.description ?? '',
        color: tagToEdit.color ?? '#000000',
      };
    }

    return {
      name: '',
      description: '',
      color: '#000000',
    };
  };

  const defaultValues = getDefaultValues();

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={tagToEdit ? 'Edit Tag' : 'Add Tag'}
      width='800px'
    >
      <Formik
        enableReinitialize
        initialValues={defaultValues}
        onSubmit={async (values) => {
          setIsLoading(true);
          setError(null);

          try {
            const requestObject = {
              name: values.name,
              description: values.description,
              color: values.color,
            };

            if (tagToEdit) {
              await updateTag({
                ...requestObject,
                id: tagToEdit.id as number,
              });
              toast.success('Tag updated successfully');
            } else {
              await createTag(requestObject);
              toast.success('Tag added successfully');
            }

            onSaved();
          } catch (err) {
            setError(err);
            toast.error('Error adding or updating tag');
          } finally {
            setIsLoading(false);
          }
        }}
      >
        {({ handleSubmit, values, setFieldValue }) => (
          <form onSubmit={handleSubmit} className='food-modal-form'>
            <TextInput
              name='name'
              title='Name *'
              placeholder='Enter name'
              value={values.name}
              onChange={(e) => setFieldValue('name', e.target.value)}
              isError={false}
            />

            <TextArea
              name='description'
              title='Description'
              placeholder='Enter description'
              value={values.description}
              onChange={(e) => setFieldValue('description', e.target.value)}
              isError={false}
            />

            <div className='color-field'>
              <label htmlFor='color'>Color *</label>

              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                }}
              >
                <input
                  id='color'
                  name='color'
                  type='color'
                  value={values.color}
                  onChange={(e) => setFieldValue('color', e.target.value)}
                  style={{
                    width: '48px',
                    height: '48px',
                    border: 'none',
                    cursor: 'pointer',
                  }}
                />

                <TextInput
                  name='colorText'
                  placeholder='#000000'
                  value={values.color}
                  onChange={(e) => setFieldValue('color', e.target.value)}
                  isError={false}
                />
              </div>
            </div>

            <div className='btn-wrapper'>
              <Button
                text={tagToEdit ? 'Save changes' : 'Add Tag'}
                type='submit'
                isDisabled={!values.name || !values.color}
                isLoading={isLoading}
              />

              {error && (
                <div className='error-message'>
                  Something went wrong. Please try again.
                </div>
              )}
            </div>
          </form>
        )}
      </Formik>
    </Modal>
  );
};

export default TagsModal;
